/**
 * Where the shell keeps the token, and the loop guard that stops it asking forever.
 *
 * **`sessionStorage`, not `localStorage`.** ICRM uses `localStorage`; this is a deliberate
 * divergence. A bearer token in `localStorage` survives the tab, is shared across every
 * tab on the origin, and lives until something removes it. In `sessionStorage` it dies
 * with the tab, which costs one silent redirect through the issuer when a second tab
 * opens and buys a much smaller window for anything that manages to run script on this
 * origin. Note that `@inai/inventory-core` touches neither — the module never reads a
 * credential out of a store it does not own, and the shell is the owner here because
 * standalone there is no host to be one.
 *
 * **The loop guard is the other half of "the module refuses to fail open".** A service
 * with no auth configured logs `auth.not_configured` and 401s every tenanted route, with
 * `code: "unauthenticated"`, no matter how good the token is. A shell that answers every
 * 401 by re-authenticating would bounce the user through the issuer forever, each round
 * trip looking like a flicker and none of them fixing anything. Counting attempts inside
 * a window turns that into a message.
 */

const TOKEN_KEY = 'inai-inventory.token';
const ATTEMPTS_KEY = 'inai-inventory.signin-attempts';

/** Three redirects inside two minutes is not a person having trouble; it is a loop. */
export const MAX_SIGNIN_ATTEMPTS = 3;
export const SIGNIN_WINDOW_MS = 120_000;

function storage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    // Third-party-cookie-blocked iframes and some privacy modes throw on access.
    return null;
  }
}

export function loadToken(): string | null {
  const value = storage()?.getItem(TOKEN_KEY);
  return value && value.length > 0 ? value : null;
}

export function saveToken(token: string): void {
  storage()?.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  storage()?.removeItem(TOKEN_KEY);
}

interface AttemptRecord {
  count: number;
  firstAt: number;
}

function readAttempts(): AttemptRecord {
  const raw = storage()?.getItem(ATTEMPTS_KEY);
  if (!raw) return { count: 0, firstAt: 0 };
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      const record = parsed as Partial<AttemptRecord>;
      if (typeof record.count === 'number' && typeof record.firstAt === 'number') {
        return { count: record.count, firstAt: record.firstAt };
      }
    }
  } catch {
    /* a corrupted counter is a zero counter */
  }
  return { count: 0, firstAt: 0 };
}

/**
 * Record an intent to send the user to the issuer, and say whether it is still sane.
 *
 * Returns false once the window has seen too many. The caller must then show a
 * configuration message instead of navigating — see `ConfigurationNotice`.
 */
export function recordSignInAttempt(now: number = Date.now()): boolean {
  const previous = readAttempts();
  const withinWindow = previous.firstAt > 0 && now - previous.firstAt < SIGNIN_WINDOW_MS;
  const next: AttemptRecord = withinWindow
    ? { count: previous.count + 1, firstAt: previous.firstAt }
    : { count: 1, firstAt: now };
  storage()?.setItem(ATTEMPTS_KEY, JSON.stringify(next));
  return next.count <= MAX_SIGNIN_ATTEMPTS;
}

export function signInAttemptCount(): number {
  const record = readAttempts();
  return Date.now() - record.firstAt < SIGNIN_WINDOW_MS ? record.count : 0;
}

/** Called once the service has actually answered as this tenant. The loop is broken. */
export function clearSignInAttempts(): void {
  storage()?.removeItem(ATTEMPTS_KEY);
}
